import React, { useState, useEffect } from "react";
import queryString from "query-string";
import Batches from "./component";
import { useHotkeys } from "../../tools/react-hotkeys-hook";
import LabelerSnackbar from "../common/SnackBar";
import constants from "../../tools/constants";
import withContext from "../../tools/withContext";
import BatchType from "../../types/Batch";
import UserType from "../../types/User";
import useRouter from "../../tools/useRouter";
import lowerMapper from "../../tools/lowerMapper";
import { toModelPredictionUploadBody } from "../../tools/modelPredictions";

const BatchContainer = (props: any) => {
  const router = useRouter();
  const {
    ctx: { appFetch },
    match: {
      params: { noPage },
    },
    location: { search },
  } = props;

  const {
    PROJECT_BATCHES_PATH,
    USER_USERS_PATH,
    BATCHES_PATH,
    USER_BATCHES_PATH,
    BATCHES_AFFECTATION_PATH,
    BATCHES_DEADLINE_PATH,
    IMAGES_PATH,
    MODEL_PREDICTIONS_PATH,
  } = constants.paths;

  const [users, setUsers] = useState<UserType[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [batches, setBatches] = useState<BatchType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [keyEvent, setKeyEvent] = useState<any>({ shiftKey: false });

  useHotkeys("*", (e) => {
    if (e.key === "Shift") {
      setKeyEvent({
        shiftKey: e.type === "keydown",
      });
    }
  });

  // map batches to make sorting information easily accessible ( labeler, project )
  const batchMapper = (batch: BatchType) => {
    return {
      ...batch,
      projectName: batch.project.name,
      labelerStr: batch.labeler
        ? `${batch.labeler.lastName} ${batch.labeler.firstName}`.trim()
        : "<non attribué>",
      labelerId: batch.labeler ? batch.labeler.id : undefined,
    };
  };

  const fetchDatas = async () => {
    setIsLoading(true);
    setBatches([]);
    try {
      setIsError(false);

      const lowerMap: any = {
        name: "lowerName",
        // "filterFields.projectName": "filterFields.lowerProjectName",
        // "filterFields.labelerFirstName": "filterFields.lowerLabelerFirstName",
      };

      const { batchSearchMode, labels, ...queryForApi } = router.query;

      const params = queryString.stringify(
        {
          ...lowerMapper(queryForApi, lowerMap),
          page: noPage,
        },
        { arrayFormat: "none" }
      );
      
      const [usersData, batchData] = await Promise.all([
        appFetch(`${USER_USERS_PATH}?size=1000`
          // ,null, null, true // API Mocked
        ),
        appFetch(`${BATCHES_PATH}?${params}`
          // ,null, null, true // API Mocked
        ),
      ]);
      setUsers(usersData.content);

      // BATCHES_PATH returns the data in the content property of an object
      // but USER_BATCHES_PATH returns a direct array
      const {
        content,
        number,
        totalPages,
        first,
        last,
        totalElements,
      } = batchData;
      const arrayDatas: BatchType[] = content || batchData;
      const batchesLoaded = arrayDatas.map((batch: any) => batchMapper(batch));
      setBatches(batchesLoaded);
      setPagination({ number, totalPages, first, last, totalElements });
      setIsLoading(false);
    } catch (e) {      
      setIsError(true);
      setIsLoading(false);
      setMessage((e as Error).message);
      setIsSnackbarOpen(true);

      setUsers([]);
      setBatches([]);
    }
  };

  const saveBatch = async (batch: BatchType) => {
    setIsLoading(true);

    try {
      setIsError(false);
      const { id, labelerId, name, state, deadline } = batch;
      const batchS: BatchType = await appFetch(BATCHES_PATH, {
        method: id ? "PUT" : "POST",
        body: JSON.stringify({
          id,
          labelerId,
          name,
          state,
          deadline,
        }),
        headers: { "Content-Type": "application/json" },
      });

      const batchInserted = batchMapper(batchS);
      setBatches(
        id
          ? batches.map((b: any) => (b.id === id ? batchInserted : b))
          : //@ts-ignore
            batches.concat([batchS])
      );
      setIsLoading(false);
      setMessage(`Batch modifié`);
      setIsError(false);
      setIsSnackbarOpen(true);
    } catch (e) {
      console.log(e);
      setIsError(true);
      setMessage((e as Error).message);
      setIsSnackbarOpen(true);
    }
  };

  const massUpdate = async (payload: any) => {
    const { ids, deadline, labelerId } = payload;
    const isLabelerUpdate = typeof labelerId !== "undefined";
    const body: any = { ids };

    if (isLabelerUpdate) {
      body.labelerId = labelerId;
    } else {
      body.deadline = deadline;
    }

    setIsLoading(true);
    setIsError(false);

    try {
      const batchS: BatchType[] = await appFetch(
        isLabelerUpdate ? BATCHES_AFFECTATION_PATH : BATCHES_DEADLINE_PATH,
        {
          method: "PUT",
          body: JSON.stringify(body),
          headers: { "Content-Type": "application/json" },
        }
      );

      setBatches(
        batches.map((b: any) => {
          if (ids.indexOf(b.id) === -1) {
            return b;
          }

          const bUpdt = batchS.find((bS: any) => bS.id === b.id);
          if (!bUpdt) {
            throw new Error("Réponse serveur non conforme");
          }

          return batchMapper(bUpdt);
        })
      );
      setIsLoading(false);
      setMessage(`Batch modifié`);
      setIsError(false);
      setIsSnackbarOpen(true);
    } catch (e) {
      console.log(e);
      setIsError(true);
      setMessage((e as Error).message);
      setIsSnackbarOpen(true);
    }
  };

  const deleteDeadline = async (u: BatchType) => {
    setIsLoading(true);
    try {
      setIsError(false);
      await appFetch(`${BATCHES_PATH}/${u.id}/deadline`, { method: "DELETE" });

      //@ts-ignore
      setBatches(
        batches.map((batch: BatchType) =>
          batch.id !== u.id ? batch : { ...batch, deadline: null }
        )
      );
      setIsLoading(false);
    } catch (e) {
      setIsError(true);
      setMessage((e as Error).message);
      setIsSnackbarOpen(true);
    }
  };

  const applyLabeling = async (
    payload: {
      batchId: number;
      projectId: string;
      modelName: string;
      modelVersion: string;
      predictions: Array<{ id: string; labels: any }>;
    },
    onProgress: (saved: number, total: number) => void
  ) => {
    const predictions = payload.predictions || [];
    const total = predictions.length;

    if (total === 0) {
      throw new Error("Aucune prédiction dans le fichier JSON");
    }

    try {
      setIsError(false);
      onProgress(0, total);

      await appFetch(MODEL_PREDICTIONS_PATH, {
        method: "POST",
        body: JSON.stringify(toModelPredictionUploadBody(payload)),
        headers: { "Content-Type": "application/json" },
      });

      onProgress(total, total);
      await fetchDatas();
      setMessage("Prédictions du modèle enregistrées");
      setIsSnackbarOpen(true);
    } catch (e) {
      setIsError(true);
      setMessage((e as Error).message);
      setIsSnackbarOpen(true);
      throw e;
    }
  };

  const deleteBatch = async (u: BatchType) => {
    setIsLoading(true);
    try {
      setIsError(false);
      await appFetch(`${BATCHES_PATH}/${u.id}/status`, { method: "DELETE" });

      //@ts-ignore
      setBatches(batches.filter((usr: any) => usr.id !== u.id));
      setIsLoading(false);
    } catch (e) {
      setIsError(true);
      setMessage((e as Error).message);
      setIsSnackbarOpen(true);
    }
  };

  const closeSnackbar = () => setIsSnackbarOpen(false);

  useEffect(() => {
    fetchDatas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noPage, search]);

  return (
    <React.Fragment>
      <LabelerSnackbar
        open={isSnackbarOpen}
        onClose={closeSnackbar}
        variant={isError ? "error" : "success"}
        message={
          isError
            ? `Une erreur est survenue ${message ? ` : ${message}` : ""}`
            : message
        }
      />
      <Batches
        saveBatch={saveBatch}
        pagination={pagination}
        massUpdate={massUpdate}
        deleteBatch={deleteBatch}
        deleteDeadline={deleteDeadline}
        applyLabeling={applyLabeling}
        isLoading={isLoading}
        batches={batches}
        users={users}
        keyEvent={keyEvent}
      />
    </React.Fragment>
  );
};

export default withContext(BatchContainer);
